// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { FivetranConnection } from "@/components/fivetran/fivetran";
import { VALID_SERVICES } from "@/components/fivetran/fivetran";
import { ServiceCard } from "@/components/fivetran/service-card";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type TeamMember,
  useInviteTeamMember,
  useRemoveTeamMember,
  useTeam,
  useUpdateRole,
} from "@/hooks/api/use-team";
import { useAuth } from "@/hooks/auth/use-auth";
import { type Role, usePermissions } from "@/hooks/auth/use-permissions";
import { usePageTitle } from "@/hooks/use-page-title";
import { api } from "@/lib/api";

interface Company {
  id: string;
  name: string;
  created_at: string;
  default_organization?: {
    id: string;
    name: string;
    created_at: string;
  };
}

function InviteTeamMemberDialog() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const inviteTeamMember = useInviteTeamMember();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inviteTeamMember.mutate(
      { email, role },
      {
        onSuccess: () => {
          setOpen(false);
          setEmail("");
          setRole("member");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Invite Team Member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="role" className="text-sm font-medium">
              Role
            </label>
            <Select
              value={role}
              onValueChange={(value: Role) => setRole(value)}
            >
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={inviteTeamMember.isPending}>
              {inviteTeamMember.isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RemoveTeamMemberDialog({ member }: { member: TeamMember }) {
  const [open, setOpen] = useState(false);
  const removeTeamMember = useRemoveTeamMember();
  const { user } = useAuth();

  const handleRemove = () => {
    removeTeamMember.mutate(
      {
        memberId: member.status === "pending" ? member.email : member.id,
        isPending: member.status === "pending",
      },
      {
        onSuccess: () => setOpen(false),
      },
    );
  };

  // Prevent removing yourself
  if (user?.id === member.id && member.status !== "pending") {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {member.status === "pending"
              ? "Cancel Invitation"
              : "Remove Team Member"}
          </DialogTitle>
          <DialogDescription>
            {member.status === "pending"
              ? `Are you sure you want to cancel the invitation sent to ${member.email}?`
              : `Are you sure you want to remove ${member.first_name} ${member.last_name} from the company? This action cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemove}
            disabled={removeTeamMember.isPending}
          >
            {removeTeamMember.isPending
              ? member.status === "pending"
                ? "Cancelling..."
                : "Removing..."
              : member.status === "pending"
                ? "Cancel Invitation"
                : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { data: members = [], isLoading, error: queryError } = useTeam();
  const updateRole = useUpdateRole();
  const { isAdmin } = usePermissions();
  // Get the company details from the user object
  const company = user?.company as Company | undefined;

  // Fetch Fivetran connections for the connections tab
  const { data: connections = [], isLoading: connectionsLoading } = useQuery({
    queryKey: ["fivetran-connections"],
    queryFn: async (): Promise<FivetranConnection[]> => {
      return api.get<FivetranConnection[]>("fivetran/connections/");
    },
  });

  const organization = company?.default_organization;
  const location = useLocation();
  const navigate = useNavigate();

  // Create a map of service to connection for easy lookup
  const connectionMap = new Map<string, FivetranConnection>();
  connections?.forEach((conn) => {
    connectionMap.set(conn.service_name, conn);
  });

  const currentTab = location.pathname.split("/").pop() || "user";

  // Set page title based on current tab
  usePageTitle(
    `${currentTab.charAt(0).toUpperCase() + currentTab.slice(1)} Settings`,
  );

  const handleTabChange = (value: string) => {
    navigate(`/settings/${value}`);
  };

  const handleRoleChange = (memberId: string, newRole: Role) => {
    updateRole.mutate({ memberId, role: newRole });
  };

  return (
    <div className="container space-y-4 max-w-4xl mx-auto w-full">
      <h1 className="font-heading">Settings</h1>

      <div className="space-y-6">
        <Tabs value={currentTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="user">Profile</TabsTrigger>
            <TabsTrigger value="company">Company</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="connections">Connections</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="user">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <div className="text-sm text-muted-foreground">Email</div>
                    <div className="font-medium">{user?.email}</div>
                  </div>
                  {user?.first_name && (
                    <div>
                      <div className="text-sm text-muted-foreground">
                        First Name
                      </div>
                      <div className="font-medium">{user.first_name}</div>
                    </div>
                  )}
                  {user?.last_name && (
                    <div>
                      <div className="text-sm text-muted-foreground">
                        Last Name
                      </div>
                      <div className="font-medium">{user.last_name}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="company">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Company Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Company Name
                    </div>
                    <div className="font-medium">{company?.name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Company ID
                    </div>
                    <div className="font-medium">{company?.id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Organization ID
                    </div>
                    <div className="font-medium">{organization?.id}</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Team Members</CardTitle>
                  {isAdmin && <InviteTeamMemberDialog />}
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {isLoading ? (
                      <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between py-2 border-b last:border-0"
                          >
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-[150px]" />
                              <Skeleton className="h-3 w-[200px]" />
                            </div>
                            <Skeleton className="h-9 w-[120px]" />
                          </div>
                        ))}
                      </div>
                    ) : queryError ? (
                      <div className="text-sm text-destructive">
                        {queryError instanceof Error
                          ? queryError.message
                          : "An error occurred"}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {members.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between py-2 border-b last:border-0"
                          >
                            <div className="space-y-1">
                              <div className="font-medium">
                                {member.first_name} {member.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {member.email}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isAdmin ? (
                                <>
                                  <Select
                                    defaultValue={member.role}
                                    onValueChange={(value: Role) =>
                                      handleRoleChange(member.id, value)
                                    }
                                  >
                                    <SelectTrigger className="w-[120px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="admin">
                                        Admin
                                      </SelectItem>
                                      <SelectItem value="member">
                                        Member
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <RemoveTeamMemberDialog member={member} />
                                </>
                              ) : (
                                <div className="text-sm font-medium capitalize">
                                  {member.role}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="connections">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Data Integrations</CardTitle>
                  <CardDescription>
                    Connect your data sources to start syncing.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!isAdmin ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">
                        Admin permissions are required to access connection
                        setup.
                      </p>
                    </div>
                  ) : connectionsLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {VALID_SERVICES.map((service) => (
                        <div key={service} className="border rounded-lg p-4">
                          <Skeleton className="h-6 w-32 mb-2" />
                          <Skeleton className="h-4 w-full mb-4" />
                          <Skeleton className="h-10 w-full" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {VALID_SERVICES.map((service) => {
                          const connection = connectionMap.get(service);
                          return (
                            <ServiceCard
                              key={service}
                              service={
                                service as
                                  | "salesforce"
                                  | "hubspot"
                                  | "marketo"
                                  | "gongio"
                              }
                              connection={connection}
                              size="full"
                              showLastUpdated={true}
                            />
                          );
                        })}
                      </div>
                      <div className="mt-6 text-center">
                        <p className="text-sm text-muted-foreground">
                          Each connection will be configured with optimized
                          settings for your data warehouse.
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
